import { Badge, Button, Dialog, Input, Select } from '@artosera/ui';
import { TYPES_LIEU, type TypeLieu } from '@artosera/shared';
import { Building2, Plus, Store, Tent, UserRound, type LucideIcon } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useArchiverLieu, useCreerLieu, useLieux, type LieuPresente } from '../../api/lieux.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ChampsManquants } from '../ChampsManquants.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { useFermetureGardee } from '../useFermetureGardee.js';
import { LIMITES } from '../validations.js';
import styles from './LieuxPage.module.css';

/** Un pictogramme par type de lieu : la galerie, un confrère, une foire, un client. */
const ICONES_TYPE: Record<TypeLieu, LucideIcon> = {
  interne: Building2,
  externe: Store,
  foire: Tent,
  client: UserRound,
};

/** Réglages › Lieux (mission S3-a, point 5) : CRUD simple. */
export function LieuxPage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const peutEcrire = galerieALaction(galerie, 'settings:locations');

  const lieux = useLieux(galerieSlug, true);
  const creer = useCreerLieu(galerieSlug);
  const archiver = useArchiverLieu(galerieSlug);

  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const [type, setType] = useState<TypeLieu>('interne');
  const formulaire = useErreurFormulaire();
  const [aArchiver, setAArchiver] = useState<LieuPresente | null>(null);

  async function soumettre(): Promise<void> {
    formulaire.reinitialiser();
    try {
      await creer.mutateAsync({ nom, type });
      setDialogueOuvert(false);
      setNom('');
    } catch (error) {
      formulaire.appliquer(error, ['nom', 'type']);
    }
  }

  const garde = useFermetureGardee(nom.trim().length > 0, setDialogueOuvert);

  return (
    <div className={styles.page}>
      <div className={styles.entete}>
        <div>
          <h1 className={styles.titre}>{t('regie:lieux.titre')}</h1>
          <p className={styles.sousTitre}>{t('regie:lieux.sousTitre')}</p>
        </div>
        {peutEcrire ? (
          <Button size="sm" onClick={() => setDialogueOuvert(true)}>
            <Plus size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:lieux.nouveauLieu')}
          </Button>
        ) : null}
      </div>

      {lieux.data?.length === 0 ? (
        <p className={styles.vide}>{t('regie:lieux.aucunResultat')}</p>
      ) : (
        <div className={styles.cadre}>
          <table className={styles.tableau}>
            <thead>
              <tr>
                <th scope="col">{t('regie:lieux.colonneNom')}</th>
                <th scope="col">{t('regie:lieux.colonneType')}</th>
                <th scope="col">{t('regie:lieux.colonneStatut')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {lieux.data?.map((lieu) => {
                const IconeType = ICONES_TYPE[lieu.type];
                return (
                  <tr key={lieu.id} data-actif={lieu.actif ? 'true' : 'false'}>
                    <td className={styles.celluleNom}>
                      <span className={styles.nomBloc}>
                        <span className={styles.lieuIcone} aria-hidden="true">
                          <IconeType size={18} strokeWidth={1.5} />
                        </span>
                        <span className={styles.lieuNom}>{lieu.nom}</span>
                      </span>
                    </td>
                    <td className={styles.celluleType}>{t(`regie:lieux.types.${lieu.type}`)}</td>
                    <td className={styles.celluleStatut}>
                      <Badge tone={lieu.actif ? 'available' : 'neutral'}>
                        {lieu.actif ? t('regie:lieux.actif') : t('regie:lieux.inactif')}
                      </Badge>
                    </td>
                    <td className={styles.celluleAction}>
                      {peutEcrire && lieu.actif ? (
                        <Button variant="ghost" size="sm" onClick={() => setAArchiver(lieu)}>
                          {t('regie:lieux.desactiver')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={dialogueOuvert}
        onOpenChange={garde.onOpenChange}
        title={t('regie:lieux.nouveauLieu')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => garde.onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void soumettre()}
              loading={creer.isPending}
              disabled={!nom.trim()}
            >
              {t('regie:lieux.creer')}
            </Button>
          </>
        }
      >
        <div className={styles.formulaire}>
          <Field
            label={t('regie:lieux.nomChamp')}
            htmlFor="lieu-nom"
            error={formulaire.messageChamp('nom')}
          >
            <Input
              id="lieu-nom"
              maxLength={LIMITES.lieu.nom}
              value={nom}
              onChange={(event) => setNom(event.target.value)}
            />
          </Field>
          <Field label={t('regie:lieux.typeChamp')} htmlFor="lieu-type">
            <Select
              id="lieu-type"
              aria-label={t('regie:lieux.typeChamp')}
              value={type}
              onValueChange={(valeur) => setType(valeur as TypeLieu)}
              options={TYPES_LIEU.map((code) => ({
                value: code,
                label: t(`regie:lieux.types.${code}`),
              }))}
            />
          </Field>
          <ChampsManquants champs={nom.trim() ? [] : [t('regie:lieux.nomChamp')]} />
          {formulaire.erreur ? (
            <p className={styles.erreur} role="alert">
              {formulaire.erreur}
            </p>
          ) : null}
        </div>
      </Dialog>
      {garde.confirmation}

      <Dialog
        open={aArchiver !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setAArchiver(null);
        }}
        title={t('regie:lieux.desactiverConfirmationTitre')}
        description={t('regie:lieux.desactiverConfirmationTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAArchiver(null)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={archiver.isPending}
              onClick={() => {
                if (aArchiver)
                  archiver.mutate(aArchiver.id, { onSuccess: () => setAArchiver(null) });
              }}
            >
              {t('regie:lieux.desactiver')}
            </Button>
          </>
        }
      >
        <p>{aArchiver?.nom}</p>
      </Dialog>
    </div>
  );
}
